/**
 * Reporting module type definitions.
 *
 * These types describe a JSON-serializable report *definition* (the saved
 * configuration that drives query translation) and the *result* model produced
 * by executing that definition against the local SQLite replica.
 *
 * Design references:
 * - docs/features/reporting/REPORTING_MODULE_DESIGN.md
 * - docs/features/reporting/REPORTING_MODULE_IMPLEMENTATION_PLAN.md
 *
 * Phase 0 scope: define the contracts only. Query translation, execution and
 * the Formly builder consume these types in later phases.
 */

/**
 * Which data the report draws from.
 * - `single_form`: one form, enabling dynamic (form-specific) field access.
 * - `all_forms_metadata`: cross-form admin overview using shared metadata only
 *   (no dynamic fields). See open decision #1 in the implementation plan for
 *   which phase this ships in.
 */
export type ReportFormScope = 'single_form' | 'all_forms_metadata';

/** Saved-report visibility. Default is decided per open-decision #3. */
export type ReportVisibility = 'private' | 'org_shared';

/**
 * Shared metadata fields available for filtering/columns regardless of form.
 * These map to columns on `interaction_records` (plus derived joins).
 */
export type ReportMetadataField =
  | 'checkpoint' // interaction_records.asset_id
  | 'form' // interaction_records.interaction_type_id
  | 'asset_instance' // interaction_records.asset_instance_id
  | 'created_by' // interaction_records.created_by
  | 'created_at'; // interaction_records.created_at

/**
 * The value-type a filter/column operates on. Operators are constrained by
 * this type (see REPORT_OPERATORS_BY_TYPE). Dynamic form fields are mapped to
 * one of these at builder time based on the form definition.
 */
export type ReportFieldType = 'text' | 'number' | 'date' | 'boolean' | 'enum';

/** Filter operators, intentionally kept small and type-constrained for v1. */
export type ReportFilterOperator =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'starts_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_empty'
  | 'is_not_empty';

/**
 * Allowed operators per field type. Used by the builder to restrict operator
 * choices and by the query engine to validate a definition before execution.
 */
export const REPORT_OPERATORS_BY_TYPE: Record<ReportFieldType, ReportFilterOperator[]> = {
  text: ['eq', 'neq', 'contains', 'starts_with', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  boolean: ['eq', 'neq', 'is_empty', 'is_not_empty'],
  enum: ['eq', 'neq', 'in', 'not_in', 'is_empty', 'is_not_empty'],
};

/** JSON-serializable filter value. `between` uses a two-element tuple. */
export type ReportFilterValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number>
  | [string | number, string | number];

/** A filter on a shared metadata field. */
export interface ReportMetadataFilter {
  field: ReportMetadataField;
  fieldType: ReportFieldType;
  operator: ReportFilterOperator;
  value: ReportFilterValue;
}

/**
 * A filter on a dynamic (form-specific) field. `fieldKey` is the form field's
 * key within `interaction_records.data` JSON. `fieldType` is resolved from the
 * form definition and drives operator/coercion rules.
 */
export interface ReportDynamicFilter {
  fieldKey: string;
  fieldType: ReportFieldType;
  operator: ReportFilterOperator;
  value: ReportFilterValue;
}

/** A column selected for output — either shared metadata or a dynamic field. */
export interface ReportColumn {
  /** `metadata` reads a shared column; `dynamic` extracts from data JSON. */
  source: 'metadata' | 'dynamic';
  /** Metadata field name, or the dynamic field key within data JSON. */
  key: string;
  /** Value type, used for formatting and export cell typing. */
  fieldType: ReportFieldType;
  /** Optional display label override (defaults to the field's label). */
  label?: string;
}

/** Single-column sort (multi-sort is a v2 enhancement). */
export interface ReportSort {
  key: string;
  source: 'metadata' | 'dynamic';
  direction: 'asc' | 'desc';
}

/**
 * Aggregation mode. `none` returns detail rows. Numeric aggregations
 * (sum/avg/min/max) require a numeric target field; count/distinct_count do not
 * (distinct_count still needs a target field).
 */
export type ReportAggregationMode =
  | 'none'
  | 'count'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'distinct_count';

export interface ReportAggregation {
  mode: ReportAggregationMode;
  /** Target field key for sum/avg/min/max/distinct_count (unused for count). */
  targetKey?: string;
  targetSource?: 'metadata' | 'dynamic';
}

/** Optional single group-by (multi-level grouping is a v2 enhancement). */
export interface ReportGroupBy {
  key: string;
  source: 'metadata' | 'dynamic';
  /** Dynamic fields carry their literal field label through (metadata labels are i18n keys). */
  label?: string;
}

/**
 * Optional row-reduction to answer state-style questions ("currently checked
 * out"). Keeps the latest N rows per checkpoint-instance before subsequent
 * filtering/aggregation. v1 reduction key is fixed to checkpoint +
 * asset_instance_id (see open-decision #2).
 */
export interface ReportLatestPerInstance {
  enabled: boolean;
  /** Number of latest rows to keep per key. Default 1. */
  n: number;
}

/** Preset relative time windows plus an explicit custom range. */
export type ReportTimeRangePreset =
  | 'all_time'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'last_7_days'
  | 'last_30_days'
  | 'custom';

export interface ReportTimeRange {
  preset: ReportTimeRangePreset;
  /** Epoch millis; required when preset === 'custom'. */
  startMs?: number;
  /** Epoch millis; required when preset === 'custom'. */
  endMs?: number;
}

/**
 * The full, JSON-serializable report definition. Persisted in Phase 2; in
 * Phase 0/1 these are constructed in code and passed to the query engine.
 */
export interface ReportDefinition {
  /** Stable id (present once saved). */
  id?: string;
  name: string;
  description?: string;

  formScope: ReportFormScope;
  /** Required when formScope === 'single_form'. */
  formId?: string;

  /** Scope selectors (empty array / undefined means "all"). */
  checkpointIds?: string[];
  userIds?: string[];
  timeRange: ReportTimeRange;

  latestPerInstance?: ReportLatestPerInstance;
  /** "Latest at or before" cutoff (epoch millis) for as-of state snapshots. */
  asOfTimestampMs?: number;

  metadataFilters: ReportMetadataFilter[];
  dynamicFilters: ReportDynamicFilter[];
  selectedColumns: ReportColumn[];
  sort: ReportSort[];

  aggregation: ReportAggregation;
  groupBy?: ReportGroupBy;

  /** Export template profile (Phase 4). */
  exportTemplateId?: string;
  visibility: ReportVisibility;

  /** Set on reports created by an onboarding sample scenario (see SampleScenarioService). */
  sampleScenario?: string;
}

/**
 * Report execution result. Rows are keyed by column key; values are already
 * coerced to their `fieldType`. `summary` carries headline metrics for the
 * results summary bar.
 */
export interface ReportResultColumn {
  key: string;
  label: string;
  fieldType: ReportFieldType;
}

export type ReportCellValue = string | number | boolean | null;

export interface ReportResult {
  columns: ReportResultColumn[];
  rows: Record<string, ReportCellValue>[];
  summary: ReportResultSummary;
}

export interface ReportResultSummary {
  rowCount: number;
  /** Aggregation output when aggregation.mode !== 'none'. */
  aggregateValue?: number | null;
  /** Effective time window applied, for display and export metadata. */
  timeRange?: { startMs: number | null; endMs: number | null };
}

/**
 * A persisted report definition (Phase 2). Wraps a {@link ReportDefinition}
 * with storage metadata. `visibility` mirrors the definition's visibility and is
 * promoted to a column for listing/filtering.
 */
export interface SavedReport {
  id: string;
  name: string;
  visibility: ReportVisibility;
  definition: ReportDefinition;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
}

/**
 * Calculated-field compatibility (mirrors REPORTING_MODULE_DESIGN.md §6.4).
 *
 * Calculated outputs are persisted as canonical numeric scalars (or null), and
 * date differences are stored in seconds (display format is a UI concern only).
 * There is no conditional/state-machine calculation kind in v1, so status-style
 * reports must rely on an explicit status/action field plus latest-per-instance
 * reduction rather than derived state.
 */
export const REPORT_SUPPORTED_CALCULATED_KINDS = [
  'date_diff_between_fields',
  'date_diff_from_previous',
  'arithmetic_two_fields',
  'checklist_total_metric',
] as const;

export type ReportSupportedCalculatedKind = (typeof REPORT_SUPPORTED_CALCULATED_KINDS)[number];
